<script setup lang="ts">
import { LicenseGaugeStatus } from "~/modules/license/models/LicenseGaugeStatus.enum";
import type { LicenseResourceUsage } from "~/modules/license/models/LicenseResourceUsage.interface";

const { t } = useI18n();

const props = defineProps<{
  criteria: LicenseResourceUsage;
  currentNumber: number | undefined;
  successColor?: string;
  name?: string;
}>();

const licenseGaugeStatus = computed(() => {
  let status: LicenseGaugeStatus = LicenseGaugeStatus.OK;

  if (
    props.criteria.tolerated !== undefined &&
    props.criteria.contracted !== undefined &&
    props.currentNumber !== undefined &&
    props.criteria.tolerated >= props.criteria.contracted &&
    props.currentNumber > props.criteria.contracted &&
    props.currentNumber < props.criteria.tolerated
  ) {
    status = LicenseGaugeStatus.WARNING;
  } else if (
    props.criteria.contracted !== undefined &&
    props.currentNumber !== undefined &&
    ((!props.criteria.tolerated &&
      props.currentNumber >= props.criteria.contracted) ||
      (props.criteria.tolerated &&
        props.criteria.tolerated >= props.criteria.contracted &&
        props.currentNumber >= props.criteria.tolerated))
  ) {
    status = LicenseGaugeStatus.ERROR;
  }

  return status;
});
</script>

<template>
  <div class="flex items-center gap-5">
    <!-- case when contracted is undefined => unlimited -->
    <template v-if="criteria.contracted === undefined">
      <span v-if="name" class="text-gray-700 font-medium basis-1/4"
        >{{ name }}
      </span>
      <span class="basis-3/4">{{ t("license.withoutLimits") }}</span>
    </template>
    <!-- case when contracted is defined => limited (contracted = 0 is a normal use case) -->
    <template v-else>
      <div class="basis-1/4 text-gray-700 font-medium">
        <template v-if="name">
          <span>{{ name }}</span>
          <span>&nbsp;:&nbsp;</span>
        </template>
        <span>{{ currentNumber }}</span>
        <span>&nbsp;/&nbsp;</span>
        <span>{{ criteria.contracted }}</span>
      </div>

      <license-content-properties-gauge-value
        class="basis-2/4"
        :criteria="criteria"
        :license-gauge-status="licenseGaugeStatus"
        :current-number="currentNumber"
        :success-color="successColor"
      ></license-content-properties-gauge-value>

      <div class="basis-1/4 flex items-center">
        <template v-if="licenseGaugeStatus === LicenseGaugeStatus.WARNING">
          <slot name="warning" />
        </template>

        <template v-if="licenseGaugeStatus === LicenseGaugeStatus.ERROR">
          <slot name="error" />
        </template>
      </div>
    </template>
  </div>
</template>
